import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  EFFECTIVE_PROFILE_EMBED,
  resolveEffectiveOrganization,
  type ResolvedOrganization,
  type WithEffectiveEmbed,
} from "@/lib/orgEffectiveProfile";
import { setCachedOrgName } from "@/lib/navCache";
import { useActiveOrg } from "./useActiveOrg";

export interface UserOrg {
  id: string;
  name: string;
  slug: string;
  type: string;
  parent_org_id: string | null;
  inherit_branding: boolean;
  description: string | null;
  logo_url: string | null;
  cover_url: string | null;
  website: string | null;
  /** Set once, by the database, the first time the org is published. */
  first_published_at?: string | null;
}

/**
 * What one read of the active org returns: the organization's own row (what
 * the editors build their drafts from) and, beside it, the same row with its
 * effective profile written over it (office profiles 2B-4). The resolved row
 * is what the strength and readiness displays score (inventory D8), so an
 * office admin's percentage counts what the office inherits and what it said
 * it does not offer. It is never merged into a draft. `resolved` is null when
 * the effective row did not come back, which 2A's trigger makes impossible
 * for a readable organization; the scorers show nothing rather than scoring
 * the own row.
 */
export interface UserOrgRead {
  org: UserOrg;
  resolved: ResolvedOrganization<UserOrg & WithEffectiveEmbed> | null;
}

/** Query key for the active org's full row. Exported so callers can invalidate. */
export const userOrgQueryKey = (orgId: string | null) => ["user-org", orgId] as const;

/**
 * Announce that the organizations row was written, so every surface re-reads it.
 *
 * **Every successful write to the active org's row must await this.** The row is
 * one shared cache entry with a `staleTime`, so a writer that does not say it
 * wrote leaves every other consumer — the admin feed completeness card, the
 * publish nudge, the onboarding gate — rendering the pre-write row until that
 * window lapses. Saving a field in the profile editor and walking to the feed
 * reproduces the same stale-percentage class of bug this shared cache was
 * introduced to fix, just from a different writer.
 *
 * The returned promise resolves once mounted readers have refetched, so a caller
 * that reports on the result of its own write can await it and be sure the
 * number it is about to show is the number it just caused. Unmounted readers are
 * marked stale and refetch when they mount.
 */
export const useRefreshUserOrg = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (orgId: string | null | undefined): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: userOrgQueryKey(orgId ?? null) }),
    [queryClient],
  );
};

/**
 * The org the user is currently administering, plus their role in it.
 *
 * Reads the active org from {@link useActiveOrg} (the single active-org source
 * of truth) rather than an unordered `limit(1)` membership lookup, so a
 * multi-org admin's selection propagates here and to every surface that reads
 * through this hook. Single-org admins resolve to their one org exactly as
 * before; users with no admin membership (agents, admins in setup) resolve to
 * `org: null` with `isLoading: false`.
 *
 * The full org record is fetched here (select "*") because consumers depend on
 * columns beyond the lightweight switcher fields — `profile_data`,
 * `is_published`, `service_tags`, `office_locations`, etc.
 *
 * **One shared cache entry, not per-caller state.** This used to hold the row in
 * a local `useState`, which meant each of the ~30 call sites owned a private
 * copy and a private `refetch`. Refreshing through one of them left every other
 * consumer on the row it had fetched at mount. That is exactly how the org
 * onboarding wizard could `await refetchOrg()` and still render its handoff from
 * the pre-save score: the awaited refetch belonged to `useOrgOnboardingStatus`'s
 * copy, while the percentage on screen came from `useOrgProfileStrength`'s
 * separate copy inside the modal. Backing it with one keyed TanStack query makes
 * an awaited `refetch()` update every consumer at once, so the wizard handoff,
 * the admin feed card, and the profile editor cannot report different numbers
 * for the same organization.
 */
export const useUserOrg = () => {
  const { activeOrgId, activeOrg, isLoading: activeLoading } = useActiveOrg();

  const orgRole = activeOrg?.role ?? null;

  const query = useQuery({
    queryKey: userOrgQueryKey(activeOrgId),
    enabled: !!activeOrgId && !activeLoading,
    // Safe only because every write path announces itself through
    // `useRefreshUserOrg` (or this hook's own `refetch`), both of which bypass
    // staleTime. This window therefore bounds incidental refetches — mount,
    // window focus — and never how long a reader can lag behind a write. A
    // writer that skips the announcement makes this a 30-second staleness bug.
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select(`*, ${EFFECTIVE_PROFILE_EMBED}`)
        .eq("id", activeOrgId!)
        .single();

      if (error) throw error;

      const read = data as unknown as UserOrg & WithEffectiveEmbed;
      const { org_effective_profiles: _embed, ...row } = read;
      if (row?.name) setCachedOrgName(row.name);
      return { org: row as UserOrg, resolved: resolveEffectiveOrganization(read) } satisfies UserOrgRead;
    },
  });

  if (query.error) console.error("Error fetching user org:", query.error);

  // Destructured so the callback below can depend on a stable identifier rather
  // than on the whole query result object, which changes every render.
  const { refetch: refetchQuery } = query;

  /**
   * Refetch the shared row and resolve the fresh record.
   *
   * Resolves `null` when there is no active org or the refetch failed, so a
   * caller that must report on freshly saved data can tell "here is the new row"
   * apart from "I could not confirm" rather than silently reusing stale data.
   */
  const refetch = useCallback(async (): Promise<UserOrg | null> => {
    if (!activeOrgId) return null;
    const result = await refetchQuery();
    if (result.isError) return null;
    return result.data?.org ?? null;
  }, [activeOrgId, refetchQuery]);

  /**
   * Refetch the shared read and resolve the fresh row WITH its effective
   * profile, for a caller that must report a strength the admin just earned
   * (useOrgProfileStrength.refresh). Null when there is no active org, the
   * refetch failed, or the effective row did not come back.
   */
  const refetchResolved = useCallback(async (): Promise<UserOrgRead["resolved"]> => {
    if (!activeOrgId) return null;
    const result = await refetchQuery();
    if (result.isError) return null;
    return result.data?.resolved ?? null;
  }, [activeOrgId, refetchQuery]);

  return {
    org: activeOrgId ? (query.data?.org ?? null) : null,
    /** The active org's row with its effective profile written over it; what the strength displays score. */
    effectiveOrg: activeOrgId ? (query.data?.resolved ?? null) : null,
    orgRole,
    /**
     * The active org's row could not be read (issue #872 approval review). A
     * reader that would otherwise treat `org: null` as "no organization" can
     * tell a failed lookup from a resolved absence.
     */
    isError: !!activeOrgId && query.isError,
    // `isPending` (not `isFetching`): a background refetch keeps the loaded row
    // on screen, matching useActiveOrg's deliberate isLoading / isRefreshing
    // split, rather than blanking every admin surface.
    isLoading: activeLoading || (!!activeOrgId && query.isPending),
    refetch,
    refetchResolved,
  };
};
